import React, { createContext, useContext, useState, ReactNode } from 'react';
import { FeatureAccessMap, FeatureName, FEATURE_STATES, FeatureState, FEATURES } from '../constants/features';

// Feature flags that should default to hidden (opt-in features)
// These are different from subscription features which default to enabled
const FEATURE_FLAGS: string[] = [
  FEATURES.WORKFLOW,
  FEATURES.AUDIENCE_BUILDER,
  FEATURES.PROMPT_STUDIO,
  FEATURES.TOOL_ROUTER,
  FEATURES.APP_BUILDER,
];

interface FeatureContextType {
  features: FeatureAccessMap;
  setFeatures: (features: FeatureAccessMap) => void;
  isFeatureEnabled: (feature: FeatureName) => boolean;
  isFeatureLocked: (feature: FeatureName) => boolean;
  isFeatureHidden: (feature: FeatureName) => boolean;
  getFeatureState: (feature: FeatureName) => FeatureState;
}

const FeatureContext = createContext<FeatureContextType | undefined>(undefined);

export const FeatureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [features, setFeatures] = useState<FeatureAccessMap>({});

  const getFeatureState = (feature: FeatureName): FeatureState => {
    // If the feature is explicitly set, return that state
    if (features[feature]) {
      return features[feature];
    }
    // Feature flags default to hidden (opt-in), subscription features default to enabled
    if (FEATURE_FLAGS.includes(feature)) {
      return FEATURE_STATES.HIDDEN;
    }
    return FEATURE_STATES.ENABLED;
  };

  const isFeatureEnabled = (feature: FeatureName): boolean => {
    return getFeatureState(feature) === FEATURE_STATES.ENABLED;
  };

  const isFeatureLocked = (feature: FeatureName): boolean => {
    return getFeatureState(feature) === FEATURE_STATES.LOCKED;
  };

  const isFeatureHidden = (feature: FeatureName): boolean => {
    return getFeatureState(feature) === FEATURE_STATES.HIDDEN;
  };

  return (
    <FeatureContext.Provider
      value={{
        features,
        setFeatures,
        isFeatureEnabled,
        isFeatureLocked,
        isFeatureHidden,
        getFeatureState,
      }}
    >
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeatures = (): FeatureContextType => {
  const context = useContext(FeatureContext);
  if (context === undefined) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
};
